import { Injectable } from "@angular/core";
import { AccountAppCommand, AccountAppValue } from "@fabric-types/app-account";
import { PlatformServiceValue } from "@fabric-types/svc-platform";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { ActionWithPayload, batchActions } from "@obg/ngrx-utils";
import {
	DateService,
	DocumentRef,
	fromSecondsToMilliseconds,
	LoggingFactory,
	PlatformService,
	WindowRef,
} from "@obg/ngx.global";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { omit } from "lodash-es";
import { asyncScheduler, combineLatest, EMPTY, iif, merge, Observable, of } from "rxjs";
import {
	catchError,
	delay,
	filter,
	map,
	observeOn,
	switchMap,
	take,
	takeUntil,
	tap,
	throttleTime,
} from "rxjs/operators";

import { AppErrorAction } from "../app-error/app-error.action";
import { AppError, AppErrorType } from "../app-error/app-error.model";
import { AUTH_TRANSACTION_ACTION_TYPE } from "../auth-transaction/auth-transaction.action";
import { AuthTransactionResponseV2 } from "../auth-transaction/auth-transaction.model";
import { COMMAND_DISPATCHER_ACTION_TYPE } from "../command-dispatcher/command-dispatcher.action";
import { CommandType } from "../command-dispatcher/command-dispatcher.model";
import { GeolocationService } from "../geolocation/geolocation.service";
import { Jurisdiction } from "../jurisdiction/jurisdiction.model";
import { JurisdictionSelector } from "../jurisdiction/jurisdiction.selector";
import { LimitsReminderAction } from "../limits-reminder/limits-reminder.action";
import { MessageScopeType, MessageType } from "../message/message.model";
import { MessageService } from "../message/message.service";
import { SessionSummaryAction } from "../responsible-gaming/session-summary/session-summary.action";
import { CORE_ROUTE } from "../route";
import { CoreState } from "../state";
import { TwoFactorAuthLocalStorageService } from "../two-factor-auth/two-factor-auth-local-storage.service";

import { AUTH_ACTION_TYPE, AuthAction } from "./auth.action";
import {
	AUTH_INVALID_SESSION_ERROR_CODES,
	AUTH_MESSAGE_CODES,
	AUTH_MESSAGE_CONTEXT,
	AUTH_PERSONAL_SESSION_LIMIT_EXPIRED_ERROR_CODES,
} from "./auth.const";
import {
	AuthenticationStatus,
	LoginWithAuthenticationToken,
	SessionEndReason,
	SessionStartPayload,
	SessionStartReason,
} from "./auth.model";
import { AuthSelector } from "./auth.selector";
import { AuthService } from "./auth.service";
import { SessionCookieService } from "./session-cookie/session-cookie.service";
import { SESSION_ACTION_TYPE, SessionAction } from "./session.action";
import { SessionConfigService } from "./session.config";
import {
	CancelVerificationData,
	LoginRequest,
	LoginSuccessfulResponse,
	LogoutPayload,
	SessionCustomEvent,
	SessionErrorStatus,
	SessionResponse,
	TimeToLiveResponse,
	TwoFactorAuthLoginFailPayload,
	TwoFactorAuthLoginSuccessPayload,
} from "./session.model";
import { SessionSelector } from "./session.selector";
import { SessionService } from "./session.service";

@Injectable()
export class SessionEffect {
	logger = this.loggingFactory.get("SessionEffect");
	noOfRetries = 0;

	login$ = createEffect(() =>
		this.actions$.pipe(
			ofType<ActionWithPayload>(SESSION_ACTION_TYPE.login),
			map((x: ActionWithPayload<LoginRequest>) => x.payload),
			switchMap((loginRequest) => {
				if (!this.config.isAuthTransactionCallNeeded()) {
					return of(loginRequest);
				}
				return this.sessionService.authenticationTransactionV2(loginRequest).pipe(
					tap((authTransactionResponse) => {
						if (this.config.isGeofencingEnabled() && this.geolocationService.isBrazilGeofencingEnabled()) {
							// This ensures that any tokens stored from radar are cleared before starting a new session
							this.geolocationService.stopGeolocationTracking();
							this.geolocationService.setGeolocationGuid(authTransactionResponse.customerId);
						}
					}),
					switchMap((authTransactionResponse) => {
						return iif(
							() =>
								this.config.isGeofencingEnabled() &&
								this.geolocationService.isBrazilGeofencingEnabled(),
							combineLatest([
								this.geolocationService.getPosition$().pipe(
									take(1),
									switchMap((position) => {
										if (position.coords) {
											return this.geolocationService
												.checkIfTheUserIsFraud$("LOGIN", authTransactionResponse.customerId)
												.pipe(
													take(1),
													switchMap((geoFraudDetails) => {
														return of(geoFraudDetails.isFraud);
													}),
													catchError((error: AppError) => {
														this.store.dispatch(this.action.loginFail(error));
														return EMPTY;
													})
												);
										}

										return of(position);
									}),
									catchError((error: AppError) => {
										this.store.dispatch(this.action.loginFail(error));
										return EMPTY;
									})
								),
								of(authTransactionResponse),
							]),
							of(authTransactionResponse)
						);
					}),
					map((authTransactionResponse) => {
						if (
							Array.isArray(authTransactionResponse) &&
							typeof authTransactionResponse[0] === "boolean" &&
							authTransactionResponse[0] &&
							this.config.isGeofencingEnabled() &&
							this.geolocationService.isBrazilGeofencingEnabled()
						) {
							const errorPayload: AppError = {
								type: AppErrorType.Forbidden,
								code: "E_GEOFRAUD",
								data: { code: "E_GEOFRAUD" },
							};
							this.geolocationService.stopGeolocationTracking();
							// shows error message
							this.store.dispatch(this.action.loginFail(errorPayload));
							return undefined;
						}
						let authenticationTransactionResponse: AuthTransactionResponseV2;

						if (Array.isArray(authTransactionResponse)) {
							authenticationTransactionResponse = authTransactionResponse[1];
						} else {
							authenticationTransactionResponse = authTransactionResponse;
						}

						if (loginRequest.options?.disallowLogin) {
							const isUserAllowedLogin = this.sessionService.isUserAllowedToLogin(
								authenticationTransactionResponse.customerCountry,
								loginRequest.options
							);
							if (!isUserAllowedLogin) {
								return undefined;
							}
						}
						if (this.config.isBrandRedirectionEnabled()) {
							return this.sessionService.initBrandRedirectionVerification(
								authenticationTransactionResponse,
								loginRequest?.options,
								loginRequest
							);
						}
						return loginRequest;
					})
				);
			}),
			concatLatestFrom(() => this.getAwsThreatDetectionActiveOrSkipped$()),
			filter(
				([loginRequest, isAwsThreadDetectionEnabledOrSkipped]) =>
					!!loginRequest && isAwsThreadDetectionEnabledOrSkipped
			),
			switchMap(([loginRequestParams]) => {
				const { ...params } = loginRequestParams;
				delete params.options;
				return this.service.login(params).pipe(
					map((response) => {
						return this.action.loginSuccess({
							...response,
							shouldRememberUser: params.shouldRememberUser,
							username: params.username,
						});
					}),
					catchError((error: AppError) => {
						return of(this.action.loginFail(error));
					})
				);
			})
		)
	);

	loginWithTwoFactorAuth$ = createEffect(() =>
		this.actions$.pipe(
			ofType<ActionWithPayload>(SESSION_ACTION_TYPE.loginWithTwoFactorAuth),
			switchMap((action: ActionWithPayload) =>
				this.getAwsThreatDetectionActiveOrSkipped$().pipe(
					map((isAwsThreadDetectionEnabledOrSkipped) => ({ action, isAwsThreadDetectionEnabledOrSkipped })),
					filter(({ isAwsThreadDetectionEnabledOrSkipped }) => isAwsThreadDetectionEnabledOrSkipped),
					take(1)
				)
			),
			tap(({ action }) =>
				this.twoFactorAuthLocalStorageService.setIsEnabledRememberDevice(action?.payload?.rememberDevice)
			),
			switchMap(({ action }) =>
				this.service.login(action.payload).pipe(
					map((response) =>
						this.action.loginWithTwoFactorAuthenticationSuccess({
							...response,
							shouldRememberUser: action.payload.shouldRememberUser,
							username: action.payload.username,
							method: action.payload.method,
						})
					),
					catchError((error: AppError) =>
						of(this.action.loginWithTwoFactorAuthenticationFail({ method: action.payload.method, error }))
					)
				)
			)
		)
	);

	loginWithTwoFactorAuthSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginWithTwoFactorAuthSuccess),
			map((x: ActionWithPayload<TwoFactorAuthLoginSuccessPayload>) => x.payload),
			map((payload) => omit(payload, "method")),
			map((payload) => this.action.loginSuccess(payload))
		)
	);

	loginWithTwoFactorAuthFail$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginWithTwoFactorAuthFail),
			map((x: ActionWithPayload<TwoFactorAuthLoginFailPayload>) => x.payload),
			map(({ error }) => this.action.loginFail(error))
		)
	);

	loginWithVerificationCode$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginWithVerificationCode),
			switchMap((action: ActionWithPayload) =>
				this.getAwsThreatDetectionActiveOrSkipped$().pipe(
					map((isAwsThreadDetectionEnabledOrSkipped) => ({ action, isAwsThreadDetectionEnabledOrSkipped }))
				)
			),
			filter(({ isAwsThreadDetectionEnabledOrSkipped }) => isAwsThreadDetectionEnabledOrSkipped),
			switchMap(({ action }) =>
				this.service.loginWithVerificationCode(action.payload).pipe(
					map((response) =>
						this.action.loginWithVerificationCodeSuccess({
							...response,
							username: action.payload.username,
							shouldRememberUser: action.payload.shouldRememberUser,
						})
					),
					catchError((error: AppError) => of(this.action.loginWithVerificationCodeFail(error)))
				)
			)
		)
	);

	loginWithPersonalNumber$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginWithPersonalNumber),
			switchMap((action: ActionWithPayload) =>
				this.getAwsThreatDetectionActiveOrSkipped$().pipe(
					map((isAwsThreadDetectionEnabledOrSkipped) => ({ action, isAwsThreadDetectionEnabledOrSkipped }))
				)
			),
			filter(({ isAwsThreadDetectionEnabledOrSkipped }) => isAwsThreadDetectionEnabledOrSkipped),
			switchMap(({ action }) =>
				this.service.loginWithPersonalNumber(action.payload).pipe(
					map((response) =>
						this.action.loginWithPersonalNumberSuccess({
							...response,
							username: action.payload.username,
							shouldRememberUser: action.payload.shouldRememberUser,
						})
					),
					catchError((error: AppError) => of(this.action.loginWithPersonalNumberFail(error)))
				)
			)
		)
	);

	loginWithToken$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginWithToken),
			switchMap((action: ActionWithPayload) =>
				this.getAwsThreatDetectionActiveOrSkipped$().pipe(
					take(1),
					map((isAwsThreadDetectionEnabledOrSkipped) => ({ action, isAwsThreadDetectionEnabledOrSkipped }))
				)
			),
			filter(({ isAwsThreadDetectionEnabledOrSkipped }) => isAwsThreadDetectionEnabledOrSkipped),
			switchMap(({ action }) =>
				this.service.loginWithToken(action.payload).pipe(
					map((response) =>
						this.action.loginWithTokenSuccess({
							...response,
							authenticationMethod: action.payload.authenticationMethod,
						})
					),
					catchError((error: AppError) =>
						of(
							this.action.loginWithTokenFail({
								...error,
								authenticationMethod: action.payload.authenticationMethod,
							})
						)
					)
				)
			)
		)
	);

	verifyAuthenticationToken$ = this.platformService.forClient(() =>
		createEffect(
			() =>
				this.actions$.pipe(
					ofType(RouteAction.resolveEnd),
					map((currentRoute) => [
						currentRoute?.payload?.current?.queryParams?.authenticationToken,
						currentRoute?.payload?.current?.queryParams?.pokerclientredirect,
					]),
					filter(([authenticationToken]) => !!authenticationToken),
					tap(([authenticationToken, pokerclientredirect]) => {
						if (pokerclientredirect) {
							this.store.dispatch(
								RouteAction.navigate(
									CORE_ROUTE.auth.redirectionVerify,
									{},
									{
										queryParamsHandling: "merge",
										queryParams: { pokerclientredirect: pokerclientredirect },
									}
								)
							);
						} else {
							this.store.dispatch(RouteAction.navigate(CORE_ROUTE.auth.redirectionVerify));
						}

						this.store.dispatch(this.action.loginWithAuthenticationTokenSave(authenticationToken));
						this.store.dispatch(this.action.loginWithAuthenticationToken({ authenticationToken }));
					})
				),
			{ dispatch: false }
		)
	);

	loginWithAuthenticationToken$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginWithAuthenticationToken),
			switchMap((action: ActionWithPayload) =>
				this.getAwsThreatDetectionActiveOrSkipped$().pipe(
					map((isAwsThreadDetectionEnabledOrSkipped) => ({ action, isAwsThreadDetectionEnabledOrSkipped }))
				)
			),
			filter(({ isAwsThreadDetectionEnabledOrSkipped }) => isAwsThreadDetectionEnabledOrSkipped),
			switchMap(({ action }) =>
				combineLatest([
					this.store.select(RouteSelector.getQueryParams),
					this.service.loginWithAuthenticationToken(action.payload),
				]).pipe(
					take(1),
					map(([queryParams, response]) => {
						if (queryParams?.pokerclientredirect) {
							this.windowRef.native.location.href = "htcmd:new_window?url=close.html";
						}
						return this.action.loginSuccess({ ...response });
					}),
					catchError((error: AppError) => of(this.action.loginWithAuthenticationTokenFail(error)))
				)
			)
		)
	);

	postRegistrationLoginToken$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.postRegistrationLoginWithAuthenticationToken),
			map((x: ActionWithPayload<LoginWithAuthenticationToken>) => x.payload),
			switchMap((request) =>
				this.service.loginWithAuthenticationToken(request).pipe(
					map((response) => {
						return this.action.loginSuccess({ ...response });
					}),
					catchError((error: AppError) => {
						this.noOfRetries++;
						if (this.noOfRetries >= 5) {
							return of(this.action.loginWithAuthenticationTokenFail(error));
						}
						// BE needs time to change the account status of the user, so when we are recalling the second time we are getting the error again.
						setTimeout(() => {
							this.store.dispatch(this.action.postRegistrationLoginWithAuthToken(request));
						}, 500);

						return EMPTY;
					})
				)
			)
		)
	);

	sessionInvalid$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AppErrorAction.unauthorizedError),
			map((action: ActionWithPayload<AppError>) => action.payload),
			concatLatestFrom(() => [
				this.store.select(this.selector.getSessionErrorStatus()),
				this.store.select(JurisdictionSelector.get),
			]),
			filter(
				([error, errorState, jurisdiction]) =>
					Object.values(AUTH_INVALID_SESSION_ERROR_CODES).includes(error.code) &&
					errorState !== SessionErrorStatus.ExpiryCheckInProgress &&
					!(
						jurisdiction === Jurisdiction.Ksa &&
						AUTH_PERSONAL_SESSION_LIMIT_EXPIRED_ERROR_CODES.includes(error.code)
					)
			),
			map(([error]) => error),
			concatLatestFrom(() => this.store.select(this.selector.getSessionToken())),
			switchMap(([error, sessionToken]) => {
				if (error.code === AUTH_INVALID_SESSION_ERROR_CODES.invalidSession && sessionToken) {
					return this.service.getSessionErrorResponse(sessionToken).pipe(
						map(() => {
							this.logger.error(
								"Area API authorization encryption mismatch - a different request failed to authorize with a valid session. Refer to comment."
							);
							// This has caused an incident before - see #inc-4746_rca-602
							// Issue was that the encryption key was invalid, this caused other API calls to fail even with a valid session.
							// Contact API and ask them to check the encryption key.
							return this.action.sessionInvalid({
								type: AppErrorType.UnhandledError,
								code: "500",
								data: undefined,
							});
						}),
						catchError((sessionError: AppError) => of(this.action.sessionInvalid(sessionError)))
					);
				}
				return of(this.action.sessionInvalid(error));
			})
		)
	);

	logout$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.logout),
			map((action: ActionWithPayload<LogoutPayload>) => action.payload),
			concatLatestFrom(() => [
				this.store.select(this.selector.getSessionToken()),
				this.store.select(this.selector.getSessionErrorStatus()),
			]),
			switchMap(([logoutPayload, token, sessionErrorStatus]) =>
				this.service.logout(token).pipe(
					map(() =>
						sessionErrorStatus === SessionErrorStatus.Expired ||
						sessionErrorStatus === SessionErrorStatus.Invalid
							? { ...logoutPayload, shouldSkipNotification: true }
							: logoutPayload
					),
					map((logoutSuccessPayload) => this.action.logoutSuccess(logoutSuccessPayload)),
					catchError((error: AppError) => of(this.action.logoutFail(error)))
				)
			)
		)
	);

	verify$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.verify),
			map((x: ActionWithPayload<string>) => x.payload),
			concatLatestFrom(() => this.store.select(this.selector.getSessionToken())),
			switchMap(([newToken, existingToken]) =>
				(!newToken && !existingToken ? this.convertToken$() : of(newToken || existingToken)).pipe(
					map((token) => [newToken, existingToken, token])
				)
			),
			switchMap(([newToken, existingToken, token]) =>
				this.service.verifySession(token).pipe(
					map((response) =>
						!newToken && !existingToken
							? batchActions(this.action.verifySuccess(response), this.action.loginWithCookieSuccess())
							: this.action.verifySuccess(response)
					),
					catchError((error: AppError) => of(this.action.verifyFail(error)))
				)
			)
		)
	);

	/**
	 * Polls server within configured interval, dispatches fail actions if there's request errors
	 *
	 * TODO: refactor keep alive logic in OBGB-4166
	 */
	keepAlive$ = this.platformService.forClient(() =>
		createEffect(
			() =>
				this.actions$.pipe(
					ofType(RouteAction.resolveEnd, SESSION_ACTION_TYPE.keepAlive),
					filter(() => this.config.isKeepAliveEnabled()),
					throttleTime(this.config.getKeepAliveInterval()),
					concatLatestFrom(() => this.store.select(this.selector.getSessionToken())),
					map(([, x]) => x),
					filter((sessionToken) => !!sessionToken),
					switchMap((sessionToken) =>
						this.service.keepAlive(sessionToken!).pipe(
							map((response) => this.action.keepAliveSuccess(response)),
							catchError((error: AppError) => of(this.action.keepAliveFail(error)))
						)
					)
				),
			{ dispatch: false }
		)
	);

	keepAliveExternalRequest$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.keepAlive),
			map(() => this.action.keepAlive())
		)
	);

	logoutExternalRequest$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.logout),
			map(() => SessionSummaryAction.fetch(true))
		)
	);

	keepAliveFail$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(SESSION_ACTION_TYPE.keepAliveFail),
				map((action: ActionWithPayload<AppError>) => action.payload),
				map((error) => this.action.sessionExpired(error))
			),
		{ dispatch: false }
	);

	geolocationVerifySuccess$ = createEffect(
		() => {
			return this.actions$.pipe(
				ofType(SESSION_ACTION_TYPE.verifySuccess),
				filter(() => this.config.isGeofencingEnabled() && this.geolocationService.isBrazilGeofencingEnabled()),
				concatLatestFrom(() => this.store.select(AuthSelector.isFirstLogin)),
				filter(([, isFirstLogin]) => {
					return !isFirstLogin;
				}),
				switchMap(() => {
					/**
					 * Radar automatically stores the radar-userId in localStorage.
					 * Here we are retrieving for setting the GUID of the user upon refresh
					 */

					const customerId: string | null = localStorage.getItem("radar-userId");
					return of(customerId);
				}),
				filter((customerId) => !!customerId),
				switchMap((customerId: string) => {
					this.geolocationService.setGeolocationGuid(customerId);

					return this.sessionService
						.checkIfUserIsFraudulentDuringSession$(customerId)
						.pipe(takeUntil(this.actions$.pipe(ofType(AUTH_ACTION_TYPE.sessionEnd))));
				})
			);
		},
		{ dispatch: false }
	);

	/**
	 * Verifies session after time to live expired
	 */
	sessionTimeout$ = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(
					SESSION_ACTION_TYPE.loginSuccess,
					SESSION_ACTION_TYPE.loginWithVerificationCodeSuccess,
					SESSION_ACTION_TYPE.loginWithPersonalNumberSuccess,
					SESSION_ACTION_TYPE.loginWithTokenSuccess,
					SESSION_ACTION_TYPE.verifySuccess,
					SESSION_ACTION_TYPE.keepAliveSuccess
				),
				map((x: ActionWithPayload<TimeToLiveResponse>) => x.payload),
				concatLatestFrom(() => this.store.select(JurisdictionSelector.get)),
				filter(([, jurisdiction]) => jurisdiction !== Jurisdiction.Ksa),
				switchMap(([response]) =>
					of(this.action.checkSessionExpired()).pipe(
						delay(fromSecondsToMilliseconds(response.timeToLiveSeconds)),
						takeUntil(this.actions$.pipe(ofType(AUTH_ACTION_TYPE.sessionEnd)))
					)
				)
			)
		)
	);

	resetSession$ = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(SESSION_ACTION_TYPE.sessionExpired, SESSION_ACTION_TYPE.sessionInvalid),
				delay(10),
				map(() => this.action.resetSession())
			)
		)
	);

	checkSessionExpired$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.checkSessionExpired),
			concatLatestFrom(() => this.store.select(this.selector.getSessionToken())),
			switchMap(([, sessionToken]) =>
				this.service.verifySession(sessionToken).pipe(
					map((response) => this.action.verifySuccess(response)),
					catchError((error: AppError) =>
						AUTH_PERSONAL_SESSION_LIMIT_EXPIRED_ERROR_CODES.includes(error.code)
							? of(
									batchActions(
										this.action.getNextTimeLoginSuccess(
											this.dateService.format(error.data.customerCanLogInAt, "dd/MM/yyyy HH:mm")
										),
										this.action.sessionExpired(error)
									)
								)
							: of(this.action.sessionExpired(error))
					)
				)
			)
		)
	);

	geoFraudCheck$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					SESSION_ACTION_TYPE.loginSuccess,
					SESSION_ACTION_TYPE.loginWithVerificationCodeSuccess,
					SESSION_ACTION_TYPE.loginWithPersonalNumberSuccess,
					SESSION_ACTION_TYPE.loginWithTokenSuccess
				),
				observeOn(asyncScheduler),
				filter(() => this.config.isGeofencingEnabled() && this.geolocationService.isBrazilGeofencingEnabled()),
				switchMap((action: ActionWithPayload) => {
					return this.sessionService
						.checkIfUserIsFraudulentDuringSession$(action.payload.customerId)
						.pipe(takeUntil(this.actions$.pipe(ofType(AUTH_ACTION_TYPE.sessionEnd))));
				})
			),
		{ dispatch: false }
	);

	sessionStart$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				SESSION_ACTION_TYPE.loginSuccess,
				SESSION_ACTION_TYPE.loginWithVerificationCodeSuccess,
				SESSION_ACTION_TYPE.loginWithPersonalNumberSuccess,
				SESSION_ACTION_TYPE.loginWithTokenSuccess
			),
			observeOn(asyncScheduler),
			tap((action: ActionWithPayload) =>
				this.service.sessionDomEvent(SessionCustomEvent.obgLoginSuccess, {
					isThirdPartyLogin: action.type === SESSION_ACTION_TYPE.loginWithTokenSuccess,
					customerId: action.payload?.customerId || undefined,
				})
			),
			map((action) => {
				const input = (action as ActionWithPayload).payload as LoginSuccessfulResponse;
				const payload: SessionStartPayload = {
					isFirstLogin: input.isFirstLogin,
					jurisdiction: input.jurisdiction,
					sessionLimitCheckPeriodInMillis: fromSecondsToMilliseconds(
						input.personalSessionLimitCheckPeriodSeconds
					),
					token: input.sessionToken,
					startReason: SessionStartReason.Login,
				};

				return this.config.getNextTimeLoginEnabled()
					? batchActions(this.authAction.sessionStart(payload), this.action.getNextTimeLogin())
					: this.authAction.sessionStart(payload);
			})
		)
	);

	verifySuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				SESSION_ACTION_TYPE.verifySuccess // this is also triggered on checkSessionExpired
			),
			observeOn(asyncScheduler),
			map((action) => {
				const input = (action as ActionWithPayload).payload as SessionResponse;
				const payload: SessionStartPayload = {
					token: input.sessionToken,
					startReason: SessionStartReason.TokenVerification,
				};

				return this.authAction.sessionStart(payload);
			})
		)
	);

	sessionEnd$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				SESSION_ACTION_TYPE.logoutSuccess,
				SESSION_ACTION_TYPE.loginWithVerificationCodeFail,
				SESSION_ACTION_TYPE.verifyFail,
				SESSION_ACTION_TYPE.sessionExpired,
				SESSION_ACTION_TYPE.sessionInvalid,
				SESSION_ACTION_TYPE.logoutFail
			),
			tap(() => this.service.clearSessionToken()),
			observeOn(asyncScheduler),
			map((x: ActionWithPayload) => {
				let reason: SessionEndReason | undefined;
				switch (x.type) {
					case SESSION_ACTION_TYPE.logoutSuccess:
						reason = SessionEndReason.Logout;
						break;
					case SESSION_ACTION_TYPE.sessionExpired:
						reason = SessionEndReason.SessionExpired;
						break;
					case SESSION_ACTION_TYPE.verifyFail:
						reason = SessionEndReason.VerificationFailed;
						break;
					case SESSION_ACTION_TYPE.sessionInvalid:
						reason = SessionEndReason.SessionInvalid;
						break;
				}

				this.service.sessionDomEvent(SessionCustomEvent.obgLogout, { reason });

				if (!this.geolocationService.isBrazilGeofencingEnabled()) {
					return this.authAction.sessionEnd(reason);
				}

				// when brazil geofencing is enabled
				this.geolocationService.stopGeolocationTracking();
				if ((x.payload as LogoutPayload)?.isUserFraud) {
					this.geolocationService.showGeofencingErrorAlert();
				}

				this.documentRef.obgEventBus?.dispatch(AccountAppCommand.ClearRewards);
				return this.authAction.sessionEnd(reason);
			})
		)
	);

	navigateFromRegistrationPage$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionStart),
			switchMap(() =>
				this.store.select(RouteSelector.getCurrent).pipe(
					ignoreNil(),
					take(1),
					filter(
						(route) =>
							route.name === CORE_ROUTE.registration.register &&
							!!route.queryParams?.loginFromRegistration
					),
					map((route) => {
						const redirect = route.queryParams?.lp ?? CORE_ROUTE.home;
						return RouteAction.navigate(redirect);
					})
				)
			)
		)
	);

	fetchThresholdStatus$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(AUTH_ACTION_TYPE.sessionStart),
				filter(() => !!this.documentRef.obgEventBus),
				switchMap(() =>
					new Observable(this.documentRef.obgEventBus.retrieve(AccountAppValue.FetchThresholdStatus)).pipe(
						ignoreNil()
					)
				)
			),
		{ dispatch: false }
	);

	PollNotificationsInfo$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(AUTH_ACTION_TYPE.sessionStart),
				filter(() => !!this.documentRef.obgEventBus),
				switchMap(
					() => new Observable(this.documentRef.obgEventBus.retrieve(AccountAppValue.PollNotificationsInfo))
				)
			),
		{ dispatch: false }
	);

	navigateFromSecuredOnSessionEnd$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionEnd),
			map((x: ActionWithPayload<SessionEndReason>) => x.payload),
			filter((x) => x !== SessionEndReason.VerificationFailed),
			concatLatestFrom(() => this.store.select(RouteSelector.getCurrent)),
			filter(([, current]) => !!current && this.secureAuthService.isCurrentRouteSecure(current)),
			map(([, current]) => {
				const route = current!.fallbackRoute || CORE_ROUTE.home;

				return RouteAction.navigate(route);
			})
		)
	);

	logoutCommand$ = createEffect(() =>
		this.actions$.pipe(
			ofType(COMMAND_DISPATCHER_ACTION_TYPE.trigger),
			map((x: ActionWithPayload<CommandType>) => x.payload),
			filter((x) => x === CommandType.Logout),
			map(() => SessionSummaryAction.fetch(true))
		)
	);

	afterFetchSessionSummary$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				SessionSummaryAction.fetchBeforeLogoutSuccess,
				SessionSummaryAction.fetchBeforeLogoutFailure,
				SessionSummaryAction.fetchBeforeLogoutCancel
			),
			map(() => LimitsReminderAction.fetchBeforeLogout())
		)
	);

	afterFetchLimitsReminder$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				LimitsReminderAction.fetchBeforeLogoutSuccess,
				LimitsReminderAction.fetchBeforeLogoutFailure,
				LimitsReminderAction.skip
			),
			map(() => this.action.logout())
		)
	);

	clearCustomerNotVerifiedData$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusSuccess,
				AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusFail
			),
			map(() => this.action.clearCustomerNotVerifiedData())
		)
	);

	clearCustomerNotVerifiedDataOnCancelVerification$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_TRANSACTION_ACTION_TYPE.cancelVerification),
			filter((action: ActionWithPayload<CancelVerificationData>) => !!action.payload.transactionTimeout),
			map(() => this.action.clearCustomerNotVerifiedData())
		)
	);

	getNextTimeLogin$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.getNextTimeLogin),
			switchMap(() =>
				this.service.getNextTimeLoginAfterSessionLimitReached().pipe(
					map((sessionLimitNextTimeLoginResponse) => {
						const nextTimeLogin = this.dateService.format(
							sessionLimitNextTimeLoginResponse.customerCanLogInAt,
							"dd/MM/yyyy HH:mm"
						);
						return this.action.getNextTimeLoginSuccess(nextTimeLogin);
					}),
					catchError((error: AppError) => of(this.action.getNextTimeLoginFail(error)))
				)
			)
		)
	);

	sessionToSdk$ = this.platformService.forClient(() =>
		createEffect(
			() =>
				this.store.select(this.selector.getSessionToken()).pipe(
					switchMap((token) =>
						this.store.select(this.selector.getAuthenticationStatus()).pipe(
							filter((authStatus) => authStatus !== AuthenticationStatus.Pending),
							tap(() => {
								const session = token
									? {
											token: token,
										}
									: undefined;

								this.setSession(session);
							})
						)
					)
				),
			{
				dispatch: false,
			}
		)
	);

	notifyNativeLoginFailed$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					SESSION_ACTION_TYPE.loginFail,
					SESSION_ACTION_TYPE.loginWithAuthenticationTokenFail,
					SESSION_ACTION_TYPE.loginWithVerificationCodeFail,
					SESSION_ACTION_TYPE.loginWithPersonalNumberFail,
					SESSION_ACTION_TYPE.loginWithTokenFail,
					SESSION_ACTION_TYPE.verifyFail
				),
				tap((action) => {
					/**
					 * When login in Betsson BR fails we need to stop geolocation
					 */
					if (
						action.type === SESSION_ACTION_TYPE.loginFail &&
						this.geolocationService.isBrazilGeofencingEnabled()
					) {
						this.geolocationService.stopGeolocationTracking();
					}
					this.service.sessionDomEvent(SessionCustomEvent.obgLoginFail);
				})
			),
		{ dispatch: false }
	);
	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$({
				[SESSION_ACTION_TYPE.loginFail]: AUTH_MESSAGE_CONTEXT.login,
				[SESSION_ACTION_TYPE.loginWithAuthenticationTokenFail]:
					AUTH_MESSAGE_CONTEXT.loginWithAuthenticationToken,
				[SESSION_ACTION_TYPE.loginWithVerificationCodeFail]: AUTH_MESSAGE_CONTEXT.loginWithVerificationCode,
				[SESSION_ACTION_TYPE.loginWithPersonalNumberFail]: AUTH_MESSAGE_CONTEXT.loginWithPersonalNumber,
				[SESSION_ACTION_TYPE.loginWithTokenFail]: AUTH_MESSAGE_CONTEXT.loginWithToken,
				[SESSION_ACTION_TYPE.logoutFail]: AUTH_MESSAGE_CONTEXT.logout,
				[SESSION_ACTION_TYPE.verifyFail]: AUTH_MESSAGE_CONTEXT.verify,
			}),
			this.messageService.mapClearByContext$({
				[SESSION_ACTION_TYPE.login]: AUTH_MESSAGE_CONTEXT.login,
				[SESSION_ACTION_TYPE.loginWithAuthenticationToken]: AUTH_MESSAGE_CONTEXT.loginWithAuthenticationToken,
				[SESSION_ACTION_TYPE.loginWithVerificationCode]: AUTH_MESSAGE_CONTEXT.loginWithVerificationCode,
				[SESSION_ACTION_TYPE.loginWithPersonalNumber]: AUTH_MESSAGE_CONTEXT.loginWithPersonalNumber,
				[SESSION_ACTION_TYPE.loginWithToken]: AUTH_MESSAGE_CONTEXT.loginWithToken,
				[SESSION_ACTION_TYPE.logout]: AUTH_MESSAGE_CONTEXT.logout,
				[SESSION_ACTION_TYPE.verify]: AUTH_MESSAGE_CONTEXT.verify,
				[SESSION_ACTION_TYPE.checkSessionExpired]: AUTH_MESSAGE_CONTEXT.verify,
			}),
			this.messageService.map$({
				[SESSION_ACTION_TYPE.loginWithVerificationCodeSuccess]: {
					context: AUTH_MESSAGE_CONTEXT.loginWithVerificationCode,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: AUTH_MESSAGE_CODES.login.accountValidated,
				},
			})
		)
	);

	constructor(
		private actions$: Actions,
		private action: SessionAction,
		private authAction: AuthAction,
		private selector: SessionSelector,
		private config: SessionConfigService,
		private service: SessionService,
		private secureAuthService: AuthService,
		private store: Store<CoreState>,
		private messageService: MessageService,
		private platformService: PlatformService,
		private sessionCookieService: SessionCookieService,
		private dateService: DateService,
		private loggingFactory: LoggingFactory,
		private twoFactorAuthLocalStorageService: TwoFactorAuthLocalStorageService,
		private sessionService: SessionService,
		private windowRef: WindowRef,
		private geolocationService: GeolocationService,
		private documentRef: DocumentRef
	) {}

	private getAwsWafReady$() {
		return new Observable(this.documentRef.obgEventBus.retrieve(PlatformServiceValue.AwsWAFReady));
	}

	private convertToken$(): Observable<string> {
		const getToken$ = () =>
			this.store.pipe(
				select(this.selector.getAuthenticationStatus()),
				filter((authStatus) => authStatus === AuthenticationStatus.Pending),
				switchMap(() => this.sessionCookieService.getSessionToken()),
				map((token) => (token ? token.sessionToken : ""))
			);

		return this.config.isEnabledAwsThreatDetection$().pipe(
			switchMap((isEnabled) => {
				if (!isEnabled) {
					return getToken$();
				}

				return this.getAwsWafReady$().pipe(
					filter((isReady) => isReady),
					switchMap(() => getToken$())
				);
			})
		);
	}

	private setSession(session: { token: string } | undefined) {
		// TODO: update or move the session update from OBGA to SDK when fully integration will happen
		// initial implementation is done to support this ticket https://edgealpha.corpsson.com/browse/AG-8000
		this.documentRef.obgEventBus?.dispatch(AccountAppCommand.SetSession, session); // do not remove `?` otherwise B2B breaks
	}

	private getAwsThreatDetectionActiveOrSkipped$() {
		return this.config.isEnabledAwsThreatDetection$().pipe(
			switchMap((isEnabled) =>
				iif(
					() => !!isEnabled,
					// if it is enabled in the config, we should check the script is ready
					this.getAwsWafReady$(),
					// otherwise emit a value, and continue the login process
					of(true)
				)
			)
		);
	}
}
